import { Trash2 } from "lucide-react";
import { Button } from "../ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";
import { format, parseISO, isSameDay } from "date-fns";
import { formatPrice } from "../../lib/utils";
import type { CheckoutItem, Event, RaceItem } from "../../lib/types";

interface EventGroupCardProps {
  eventId: string;
  event: Event | undefined;
  items: CheckoutItem[];
  totalPrice: number;
  currency: string;
  onRemoveAll: () => void;
  onRemoveItem: (itemId: string) => void;
}

export function EventGroupCard({
  eventId,
  event,
  items,
  totalPrice,
  currency,
  onRemoveAll,
  onRemoveItem,
}: EventGroupCardProps) {
  return (
    <Card key={eventId}>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <CardTitle className="text-xl">
              {event?.title || "Event Registration"}
            </CardTitle>
            {event && event.startsAt && event.endsAt && (
              <p className="text-sm text-gray-500 mt-1">
                {(() => {
                  const startDate = parseISO(event.startsAt);
                  const endDate = parseISO(event.endsAt);
                  const isMultiDay = !isSameDay(startDate, endDate);

                  if (isMultiDay) {
                    // Multi-day: show full date range with times
                    const startDateStr = format(startDate, "EEEE, MMMM do, yyyy");
                    const startTimeStr = format(startDate, "h:mm a");
                    const endDateStr = format(endDate, "EEEE, MMMM do, yyyy");
                    const endTimeStr = format(endDate, "h:mm a");
                    return `${startDateStr} • ${startTimeStr} - ${endDateStr} • ${endTimeStr}`;
                  } else {
                    // Single-day: show date and time range
                    const dateStr = format(startDate, "EEEE, MMMM do, yyyy");
                    const startTimeStr = format(startDate, "h:mm a");
                    const endTimeStr = format(endDate, "h:mm a");
                    return `${dateStr} • ${startTimeStr} - ${endTimeStr}`;
                  }
                })()}
              </p>
            )}
            <p className="text-gray-600 mt-2">
              {items.length} attendee{items.length !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="text-right">
            <p className="font-semibold text-lg">
              {formatPrice(totalPrice, currency)}
            </p>
            {items.length > 1 && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRemoveAll}
                className="mt-2"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Remove All
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {items.map((item) => {
            const raceItem = (item as CheckoutItem & { raceItem?: RaceItem }).raceItem;
            if (raceItem) {
              return (
                <div
                  key={item.id}
                  className="p-3 bg-gray-50 rounded-lg space-y-2"
                >
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <p className="font-medium text-gray-900">
                        Boat: {raceItem.boatClass} {raceItem.sailNumber}
                      </p>
                      {raceItem.crew?.length > 0 && (
                        <p className="text-sm text-gray-600 mt-1">
                          Crew:{" "}
                          {raceItem.crew
                            .map((c) => `${c.firstName} ${c.lastName}`.trim())
                            .join(", ")}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <p className="font-semibold">
                        {item.unitPriceCents === 0
                          ? "Free"
                          : formatPrice(item.unitPriceCents, item.currency)}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onRemoveItem(item.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            }
            const eventItem = item.eventItem!;
            return (
              <div
                key={item.id}
                className="grid grid-cols-[1fr_auto_auto] items-center gap-3 p-3 bg-gray-50 rounded-lg"
              >
                <div>
                  <p className="font-medium text-gray-900">
                    {eventItem.firstName} {eventItem.lastName}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-semibold">
                    {item.unitPriceCents === 0
                      ? "Free"
                      : formatPrice(item.unitPriceCents, item.currency)}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onRemoveItem(item.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

